import React, { useCallback, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { DebouncedInput, TextAreaAtom } from "../../../../components/atoms/atom";
import {
	selectFormState,
	selectInvoicePosition,
	selectInvoicePositionComments,
	selectInvoicePositionQuantity,
	selectLvQuantityWarningPercent,
	setInvoicePositionComments,
	updateInvoicePositionCommentsV2,
} from "../../../../store/slices/invoiceV2Slice";
import {
	formatCurrencyToLocale,
	formatQuantityToLocale
} from "../../../../utils/method";
import "./PositionRowInTable.scss";

// Component to handle individual position row with Redux in table
const PositionRowInTable = React.memo(
	({
		position,
		index,
		handleQuantityChange,
		isReadonly = false,
		t,
	}) => {
		const dispatch = useDispatch();
		const invoiceQty = useSelector(
			selectInvoicePositionQuantity(position.id),
		);
		const positionComments = useSelector(
			selectInvoicePositionComments(position.id),
		);
		// Get the full position data from Redux for type classification
		const reduxPosition = useSelector(selectInvoicePosition(position.id));
		const invoiceId = useSelector((state) => selectFormState(state)?.id);

		// Get the lvQuantityWarningPercent from ticket category (default 85%)
		const lvQuantityWarningPercent = useSelector(selectLvQuantityWarningPercent);

		const [localComment, setLocalComment] = useState(positionComments || "");

		// Sync if Redux comment changes externally
		React.useEffect(() => {
			setLocalComment(positionComments || "");
		}, [positionComments]);

		// Memoize expensive calculation
		const invPercentage = useMemo(() => {
			const lvQuantity = position.lv?.quantity || position.quantity || 0;
			return lvQuantity > 0 ? (invoiceQty / lvQuantity) * 100 : 0;
		}, [invoiceQty, position]);

		// Position type classification - determines visual indicators
		const positionType = useMemo(() => {
			// Use Redux position data for type classification if available, fallback to passed position
			const positionData = reduxPosition || position;

			// Get the fromPreviousInvoice flag from the correct location
			const fromPreviousInvoice = positionData.fromPreviousInvoice || false;
			const originalQuantity = parseFloat(
				positionData.lastAllocation?.invoiceQuantity || 0,
			);
			const currentQuantity = parseFloat(invoiceQty || 0);
			const aufmassQuantity = parseFloat(
				positionData.lastAllocation?.aufmassQuantity || 0,
			);

			if (!fromPreviousInvoice) {
				return currentQuantity > 0 ? "new" : "empty";
			} else {
				// BUSINESS RULE: For carried forward positions, using aufmass quantity is expected behavior
				// Only classify as 'modified-carried-forward' if user manually changed quantity from aufmass

				// If we have aufmass quantity and current matches it, treat as standard carried forward
				if (
					aufmassQuantity > 0 &&
					Math.abs(currentQuantity - aufmassQuantity) < 0.001
				) {
					return "carried-forward";
				}

				// Otherwise, check if quantity changed from original (manual modification)
				const hasQuantityChange =
					Math.abs(originalQuantity - currentQuantity) > 0.001; // Handle floating point precision
				return hasQuantityChange
					? "modified-carried-forward"
					: "carried-forward";
			}
		}, [position, reduxPosition, invoiceQty]); // Position indicator configuration
		const positionIndicator = useMemo(() => {
			switch (positionType) {
				case "new":
					return {
						icon: "🆕",
						color: "#28a745", // Green
						bgColor: "#d4edda",
						borderColor: "#c3e6cb",
						label: t("invoice.position-type.new"),
						description: t("invoice.position-type.new-description"),
					};
				case "carried-forward":
					return {
						icon: "📋",
						color: "#17a2b8", // Blue
						bgColor: "#d1ecf1",
						borderColor: "#bee5eb",
						label: t("invoice.position-type.carried-forward"),
						description: t(
							"invoice.position-type.carried-forward-description",
						),
					};
				case "modified-carried-forward":
					return {
						icon: "✏️",
						color: "#fd7e14", // Orange
						bgColor: "#fff3cd",
						borderColor: "#ffeaa7",
						label: t(
							"invoice.position-type.modified-carried-forward",
						),
						description: t(
							"invoice.position-type.modified-carried-forward-description",
						),
					};
				default:
					return null;
			}
		}, [positionType, t]);


		// Enhanced background color calculation - ONLY quantity-based colors
		const backgroundColorClass = useMemo(() => {
			// Use ONLY original logic for quantity-based colors
			if (!isNaN(invPercentage) && invoiceQty > 0) {
				if (invPercentage > 100) {
					return "row-bg-over-100"; // Red background for over 100%
				} else if (invPercentage === 100) {
					return "row-bg-exactly-100"; // Green background for exactly 100%
				} else if (invPercentage >= lvQuantityWarningPercent) {
					return "row-bg-warning"; // Warning/yellow background for at or above warning threshold
				} else {
					return "row-bg-with-quantity"; // Light blue background for positions with quantities (added positions)
				}
			}
			// Default alternating row colors for positions without quantities
			return index % 2 === 0 ? "row-bg-default-even" : "row-bg-default-odd";
		}, [invPercentage, invoiceQty, index, lvQuantityWarningPercent]);		// Memoize input change handler with minimum quantity validation
		const handleInputChange = useCallback(
			(position, value) => {
				if (!position || value === undefined) return;

				// Get minimum allowed quantity from Redux position data
				const minimumAllowed =
					reduxPosition?.validations?.minAllowed || 0;
				const numericValue = parseFloat(value) || 0;

				// Validate against minimum allowed quantity - removed numericValue > 0 condition
				if (minimumAllowed > 0 && numericValue < minimumAllowed) {
					// Show warning but allow the change (user might be typing)
					console.warn(
						`[Position ${position.itemNumber || position.id}] Quantity ${numericValue} is below minimum allowed ${minimumAllowed}`,
					);
				}

				handleQuantityChange(position, value);
			},
			[handleQuantityChange, reduxPosition],
		);

		// Check if current quantity is below minimum allowed - fixed to include 0 values
		const minimumAllowed = reduxPosition?.validations?.minAllowed || 0;
		const isBelowMinimum =
			minimumAllowed > 0 && invoiceQty < minimumAllowed;

		// Carried forward position data for display
		const carriedForwardInfo = useMemo(() => {
			const positionData = reduxPosition || position;
			const fromPreviousInvoice = positionData.fromPreviousInvoice || false;

			if (!fromPreviousInvoice) return null;

			const originalQuantity = parseFloat(
				positionData.lastAllocation?.invoiceQuantity || 0,
			);
			const currentQuantity = parseFloat(invoiceQty || 0);
			const aufmassQuantity = parseFloat(
				positionData.lastAllocation?.aufmassQuantity || 0,
			);
			const previousInvoiceNumber =
				positionData.lastAllocation?.previousInvoiceNumber;

			// For display logic, check if current quantity is different from aufmass quantity
			// (aufmass is what we show as the base value in the badge)
			const baseQuantity =
				aufmassQuantity > 0 ? aufmassQuantity : originalQuantity;
			const hasQuantityChange =
				Math.abs(baseQuantity - currentQuantity) > 0.001;
			const isUsingAufmass =
				aufmassQuantity > 0 &&
				Math.abs(currentQuantity - aufmassQuantity) < 0.001;

			return {
				originalQuantity,
				currentQuantity,
				aufmassQuantity,
				previousInvoiceNumber,
				hasQuantityChange,
				isUsingAufmass,
				baseQuantity, // Add base quantity for cleaner logic
			};
		}, [reduxPosition, position, invoiceQty]);

		const handleCommentBlur = useCallback(() => {
			if (localComment !== positionComments) {
				const invoicePositionId = reduxPosition?.invoicePositionId;
				if (invoiceId && invoicePositionId) {
					dispatch(updateInvoicePositionCommentsV2({
						invoiceId,
						invoicePositionId,
						positionId: position.id,
						comments: localComment,
					}));
				} else {
					// Fallback: update Redux only (e.g. new unsaved invoice)
					dispatch(setInvoicePositionComments({ positionId: position.id, comments: localComment }));
				}
			}
		}, [dispatch, invoiceId, position.id, reduxPosition, localComment, positionComments]);

		return (
			<div className={`position-row ${backgroundColorClass}`}>
					<div
						className="column col-sticky-1 position-type-column"
						title={
							positionIndicator
								? `${positionIndicator.label}: ${positionIndicator.description}`
								: ""
						}
					>
						{positionIndicator && positionType !== "empty"
							? positionIndicator.icon
							: "-"}
					</div>
					<div className="column col-sticky-2 position-number-column">
						{position.itemNumber || position.id || "-"}
					</div>
					<div
						className="column col-sticky-3 short-description-column"
						title={position.description || position.lv?.shortText || ""}
					>
						{position.description || position.lv?.shortText || "-"}
					</div>
					<div className="column numeric-column">
						{formatQuantityToLocale(position.lv?.quantity || position.quantity || 0)}
					</div>

					{/* Invoice Items - Clean */}
					<div className="column quantity-input-cell">
						<DebouncedInput
							value={invoiceQty}
							onChange={handleInputChange}
							onWheel={(e) => e.target.blur()}
							position={position}
							placeholder="0"
							disabled={isReadonly}
							title={
								invoiceQty < 0
									? t("invoice.quantity-must-be-positive")
									: minimumAllowed > 0
										? t("invoice.minimum-quantity-required", {
											minimum: minimumAllowed,
										})
										: undefined
							}
							className={`quantity-input ${!isReadonly && (isBelowMinimum || invoiceQty < 0) ? "error" : ""}`}
						/>
						{/* Show minimum quantity warning */}
						{/* Error messages section */}
						{invoiceQty < 0 && !isReadonly && (
							<div className="error-message">
								{t("invoice.quantity-must-be-positive")}
							</div>
						)}
						{isBelowMinimum && invoiceQty >= 0 && !isReadonly && (
							<div className="error-message">
								{t("invoice.min-required")}: {minimumAllowed}
							</div>
						)}
						{carriedForwardInfo && (
							<div className="carried-forward-badge-wrapper">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip
											id={`tooltip-carried-forward-${position.id}`}
											className="carried-forward-tooltip"
										>
											<div className="tooltip-content">
												<div className="tooltip-title">
													{t("invoice.carried-forward-tooltip.title")}
												</div>
												{carriedForwardInfo.aufmassQuantity >
													0 && (
														<div className="tooltip-row">
															<strong>
																{t("invoice.carried-forward-tooltip.last-aufmass")}
															</strong>{" "}
															<span className="text-info">
																{formatQuantityToLocale(
																	carriedForwardInfo.aufmassQuantity,
																)}
															</span>
														</div>
													)}
												<div className="tooltip-row">
													<strong>
														{t("invoice.carried-forward-tooltip.current-invoice")}
													</strong>{" "}
													<span className="text-warning">
														{formatQuantityToLocale(
															carriedForwardInfo.currentQuantity,
														)}
													</span>
												</div>
												{carriedForwardInfo.hasQuantityChange && (
													<div className="tooltip-row">
														<strong>
															{t("invoice.carried-forward-tooltip.change")}
														</strong>{" "}
														<span
															className={`fw-bold ${carriedForwardInfo.currentQuantity > carriedForwardInfo.baseQuantity ? "text-success" : "text-danger"}`}
														>
															(
															{carriedForwardInfo.currentQuantity >
																carriedForwardInfo.baseQuantity
																? "+"
																: ""}
															{formatQuantityToLocale(
																carriedForwardInfo.currentQuantity -
																carriedForwardInfo.baseQuantity,
															)}
															)
														</span>
													</div>
												)}
											</div>
										</Tooltip>
									}
								>
									<span className="carried-forward-badge">
										<strong>
											{formatQuantityToLocale(
												carriedForwardInfo.baseQuantity,
											)}
										</strong>
										{carriedForwardInfo.hasQuantityChange && (
											<>
												<span className="arrow">→</span>
												<strong
													className={carriedForwardInfo.currentQuantity >
														carriedForwardInfo.baseQuantity
														? "quantity-increase"
														: "quantity-decrease"}
												>
													{formatQuantityToLocale(
														carriedForwardInfo.currentQuantity,
													)}
												</strong>
											</>
										)}
									</span>
								</OverlayTrigger>
							</div>
						)}
					</div>
					<div className="column center-column font-weight-600">
						{position.unit || "-"}
					</div>

					{/* Unit Price */}
					<div className="column numeric-column font-weight-600">
						{formatCurrencyToLocale(position.unitPrice || position.lv?.unitPrice || 0)}
					</div>

					{/* Total price LV */}
					<div className="column numeric-column font-weight-600 text-success">
						{formatCurrencyToLocale(
							(position.lv?.quantity || position.quantity || 0) * (position.unitPrice || position.lv?.unitPrice || 0),
						)}
					</div>

					{/* INV Amount */}
					<div className="column numeric-column font-weight-600 text-success">
						{formatCurrencyToLocale(
							invoiceQty * (position.unitPrice || position.lv?.unitPrice || 0) ||
							position.invoice?.amount ||
							position.invoiceAmount ||
							0,
						)}
					</div>

					{/* Inv Calc */}
					<div className="column numeric-column font-weight-bold">
						{isNaN(invPercentage)
							? "0.00"
							: invPercentage.toLocaleString("de-DE", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						%
					</div>

					{/* Warning */}
					<div className="column center-column warning-column">
						{/* Quantity validation warnings */}
						{!isNaN(invPercentage) && invPercentage > 100 && (
							<div className="warning-text exceeds">
								{t(
									"hierarchical-positions.quantity-exceeds-limit",
								)}
							</div>
						)}
						{!isNaN(invPercentage) &&
							invPercentage < 100 &&
							invPercentage >= lvQuantityWarningPercent && (
								<div className="warning-text approaching">
									{t(
										"hierarchical-positions.approaching-limit",
									)}
								</div>
							)}
					</div>

					<div className="column column-comment">
						{isReadonly ? (
							<span className="comment-readonly">{positionComments || ""}</span>
						) : (
							<TextAreaAtom
								className="comment-input"
								value={localComment}
								rows={2}
								onChange={(e) => setLocalComment(e.target.value)}
								onBlur={handleCommentBlur}
								disabled={!invoiceQty}
								placeholder={invoiceQty ? t("invoice.add-position-comment") : t("invoice.comment-requires-quantity")}
							/>
						)}
					</div>
			</div>
		);
	},
);

export default PositionRowInTable;
